import type { ChangeEvent } from "react";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import { useNavigate } from "react-router";
import t from "../../../../../i18n";
import InputV2 from "@ui/Input/InputV2";
import type { DashboardModelT } from "../../schema/dashboard";
import useToasts from "@ui/Toast/useToasts";
import useInput from "@ui/lib/hooks/useInput";
import Checkbox from "@ui/Checkbox";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import { useDuplicateDashboardCallback } from "../../hook/api/dashboard";

export const ModalDashboardDuplicate = memo(
  ({
    dashboard,
    shouldRedirect,
    opened,
    close,
  }: {
    dashboard: DashboardModelT;
    shouldRedirect?: boolean;
    opened: boolean;
    close: () => void;
  }) => {
    const translations = useTranslations();
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const { value: title, setValue: setTitle } = useInput(`${dashboard.title} copy`);
    const [shouldPublishDashboard, setShouldPublishDashboard] = React.useState<boolean>(
      () => dashboard.published,
    );

    const onPublishCheckboxChange = React.useCallback(
      (e: ChangeEvent<HTMLInputElement>) => setShouldPublishDashboard(e.target.checked),
      [],
    );

    const duplicateDashboard = useDuplicateDashboardCallback();
    const duplicateCallback = useCallback(
      () =>
        duplicateDashboard({
          ...dashboard,
          title,
          published: shouldPublishDashboard,
        })
          .then(dashboard => {
            if (!dashboard) throw new Error();
            addToast("success", t("project.dashboard.actions.duplicate.success", dashboard.title));
            if (shouldRedirect)
              void navigate(
                toPath("/projects/:projectId/dashboards/:dashboardId", {
                  projectId: dashboard.projectId,
                  dashboardId: dashboard.id,
                }),
              );
            return true;
          })
          .catch(() => {
            addToast("error", t("project.dashboard.actions.duplicate.error"));
            return false;
          }),
      [
        addToast,
        dashboard,
        duplicateDashboard,
        navigate,
        shouldPublishDashboard,
        shouldRedirect,
        title,
      ],
    );

    const nameError = useMemo(() => {
      if (title.trim().length === 0)
        return t("project.dashboard.transaction.duplicate.error.empty");
      if (title.trim() === dashboard.title)
        return t("project.dashboard.transaction.duplicate.error.conflict");
      return;
    }, [dashboard.title, title]);

    useEffect(() => {
      if (opened) setShouldPublishDashboard(dashboard.published);
    }, [dashboard.published, opened]);

    return (
      <Modal
        title={t("project.dashboard.transaction.duplicate.title", translations)}
        onRequestClose={close}
        isOpen={opened}
        size="l"
      >
        <ModalForm
          onSubmit={duplicateCallback}
          onRequestClose={close}
          okButtonName={t("project.dashboard.transaction.duplicate.submit_action")}
        >
          <InputV2
            required
            label={t("project.dashboard.transaction.duplicate.name.label")}
            value={title}
            onChange={e => setTitle(e.target.value)}
            error={nameError}
            revertable
          />
          <Checkbox checked={shouldPublishDashboard} onChange={onPublishCheckboxChange}>
            {t("project.dashboards.import.options.publish", translations)}
          </Checkbox>
        </ModalForm>
      </Modal>
    );
  },
);
